<template>
  <div v-if="camerasLoaded" class="container">
    <div
      class="filter-background"
      v-if="filterToggle"
      @click="toggleFilterMenu"
    ></div>
    <div class="filter-abs">
      <div class="filter-container">
        <h1>Cameras</h1>
        <button class="filterButton" @click="toggleFilterMenu">
          Jump to...
          <div class="bgSvg"></div>
        </button>
        <transition name="fadeY">
          <div v-if="filterToggle" class="filter-body">
            <button
              v-for="groupName in groupedDeviceNames"
              :key="groupName"
              @click="scrollToGroup(groupName)"
            >
              {{ groupName }}
            </button>
          </div>
        </transition>
      </div>
    </div>

    <!-- Search -->
    <div class="search-bar">
      <input
        v-model.trim="searchQuery"
        id="search"
        type="search"
        class="search-input"
        placeholder="Search"
        aria-label="Search cameras and groups"
        @keydown.esc="searchQuery = ''"
      />
    </div>
    <p v-if="searchQuery && totalVisibleCount === 0" class="search-error">
      No matches found.
    </p>

    <div class="devices">
      <div
        v-for="groupName in visibleGroupNames"
        :key="groupName"
        class="device-group"
      >
        <h2 class="group-title" :ref="'group-' + groupName">{{ groupName }}</h2>
        <p v-if="isGroupSplit(groupName)" class="group-subtitle">
          ({{ deviceNames(groupName).join(', ').replace(/,([^,]+$)/, ' &$1') }} only)
        </p>

        <div class="cameras">
          <div
            class="card"
            v-for="entry in mergedByGroupVisible[groupName]"
            :key="entry.camera.id"
            :ref="'device-' + (entry.device && entry.device.id)"
            @click="viewCameraFromDevice(entry.device, entry.camera)"
          >
            <div
            v-if="isSoldelAdmin"
            class="statusIndicator"
            :class="lighthouseStatus(entry.device.id, entry.camera.id)"
            >
              {{ lighthouseStatusText(entry.device.id, entry.camera.id) }}
            </div>
            <div class="cameraImage">
              <img
                class="hidden"
                v-bind:key="entry.camera.id"
                v-if="entry.camera.state === 'active'"
                v-shown="loadImage"
                alt="camera"
              />
              <img v-else src="../assets/videoOff.svg" alt="camera off" />
            </div>
            <Marquee>
              <p>{{ entry.camera.name | nameDecode }}</p>
            </Marquee>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div v-else class="container">
    <div class="filter-abs">
      <div class="filter-container">
        <div class="skeleton-title"></div>
        <div class="skeleton-dropdown"></div>
      </div>
    </div>
    <div class="cameras-skeleton">
      <div
        v-for="n in 6"
        :key="n"
        style="
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          width: 100%;
        "
      >
        <div class="skeleton-title card-title"></div>
        <div class="card skeleton-card">
          <div class="skeleton-image cameraImage"></div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import activeCamIcon from '@/assets/videoIcon.svg';
import Marquee from '@/components/Marquee.vue';
import { get } from 'idb-keyval';
import { Auth } from 'aws-amplify';

export default {
  components: { Marquee },
  filters: {
    cameraState(camera) {
      return camera && camera.state === 'active' ? 'Active' : 'Offline';
    },
    nameDecode(cameraName) {
      try { return decodeURIComponent(cameraName); } catch { return cameraName; }
    },
  },
  directives: {
    shown: {
      inserted: async (el, binding, vnode) => {
        const func = vnode.context[binding.expression];
        if (typeof func === 'function') func(el, vnode.key);
      },
    },
  },
  data() {
    return {
      previews: {},
      filterToggle: false,
      searchQuery: '',
      email: '',
    };
  },
  computed: {
    camerasLoaded() {
      return this.$store.state.devices.camerasLoaded;
    },
    deviceGroups() {
      const grouped = {};
      const { deviceList = [] } = this.$store.state.devices;
      deviceList.forEach(({ name, ...rest }) => {
        const groupName = name && name.startsWith('-') === false
          ? name.split(/\s*-\s*|\s+\d+$|\s*\(\s*\d+\s*\)\s*$/)[0]
          : name;
        if (!grouped[groupName]) grouped[groupName] = [];
        grouped[groupName].push({ name, ...rest });
      });
      return grouped;
    },
    groupedDeviceNames() {
      return Object.keys(this.deviceGroups).sort((a, b) => a.localeCompare(b, 'en', { numeric: true }));
    },
    mergedByGroup() {
      return this.groupedDeviceNames.reduce((acc, groupName) => {
        const devices = this.deviceGroups[groupName] || [];
        const arr = devices
          .flatMap(({ cameras = [], ...device }) => cameras.map((camera) => ({ camera, device })))
          .sort((a, b) => a.camera.name.localeCompare(b.camera.name, 'en', { numeric: true }));
        acc[groupName] = arr;
        return acc;
      }, {});
    },

    /* ==== Search-aware views ==== */

    // Returns the list of group names to render, based on precedence:
    // 1) Group name match (show whole groups)
    // 2) Camera name match (show only matching cameras within their groups)
    // 3) Trailing number at end of query (show only devices with that number)
    visibleGroupNames() {
      const qRaw = (this.searchQuery || '').trim();
      const q = qRaw.toLowerCase();
      const allGroups = this.groupedDeviceNames;

      if (!q) return allGroups;

      // 1) Group name match (highest precedence)
      const groupMatches = allGroups.filter((g) => this.nameMatches(g, q));
      if (groupMatches.length) return groupMatches;

      // 2) Camera name match (within groups)
      const camGroups = allGroups.filter((groupName) => {
        const entries = this.mergedByGroup[groupName] || [];
        return entries.some((e) => this.nameMatches(e?.camera?.name || '', q));
      });
      if (camGroups.length) return camGroups;

      // 3) Trailing number match — now also requires the group's main name to match any text part
      const numMatch = qRaw.match(/(?:\(|\s)(\d+)\s*\)?$/);
      if (numMatch) {
        const n = parseInt(numMatch[1], 10);
        const textPart = qRaw.replace(/(?:\(|\s)\d+\s*\)?$/, '').trim().toLowerCase(); // <-- added
        const byNum = allGroups.filter((groupName) => {
          if (textPart && !this.nameMatches(groupName, textPart)) return false; // <-- added
          const entries = this.mergedByGroup[groupName] || [];
          return entries.some((e) => this.extractTrailingNumber(e?.device?.name) === n);
        });
        return byNum;
      }

      return camGroups;
    },

    // Mapping groupName -> list of entries to render, filtered per the same precedence.
    mergedByGroupVisible() {
      const qRaw = (this.searchQuery || '').trim();
      const q = qRaw.toLowerCase();
      const result = {};
      const groupMatches = q
        ? this.groupedDeviceNames.filter((g) => this.nameMatches(g, q))
        : [];

      const hasGroupMatch = groupMatches.length > 0;

      // trailing number (only used if no group match)
      const numMatch = qRaw.match(/(?:\(|\s)(\d+)\s*\)?$/);
      const n = numMatch ? parseInt(numMatch[1], 10) : null;
      const textPart = qRaw.replace(/(?:\(|\s)\d+\s*\)?$/, '').trim().toLowerCase(); // <-- added

      this.visibleGroupNames.forEach((groupName) => {
        let list = this.mergedByGroup[groupName] || [];
        if (!q || hasGroupMatch) {
          result[groupName] = list;
          return;
        }
        // Camera name match: keep only matching cameras
        list = list.filter((e) => this.nameMatches(e?.camera?.name || '', q));
        result[groupName] = list;

        if (n != null && list.length === 0) {
          // Number search: only devices with that number, but also respect the group's main name
          if (textPart && !this.nameMatches(groupName, textPart)) { // <-- added
            result[groupName] = [];
            return;
          }
          list = (this.mergedByGroup[groupName] || []).filter(
            (e) => this.extractTrailingNumber(e?.device?.name) === n,
          );
          result[groupName] = list;
        }
      });
      return result;
    },

    // For the "no matches" message
    totalVisibleCount() {
      if (!this.visibleGroupNames) return 0;
      return this.visibleGroupNames.reduce((sum, g) => {
        const arr = this.mergedByGroupVisible[g] || [];
        return sum + arr.length;
      }, 0);
    },
    userType() {
      return this.$store.state.user?.userType?.toLowerCase() ?? 'user';
    },
    isSoldelAdmin() {
      const emailPasses = this.email?.endsWith?.('@soldelgroup.com') ?? false;
      const adminPasses = this.userType.includes('admin');
      const isSoldelAdmin = emailPasses && adminPasses;
      return isSoldelAdmin;
    },
    lighthouseLoaded() {
      return this.$store.getters['lighthouse/isUidsLoaded'];
    },
    lighthouseData() {
      if (this.lighthouseLoaded) return this.$store.getters['lighthouse/uids'];
      return {};
    },
  },
  methods: {
    nameMatches(str, q) {
      if (!str) return false;
      const variants = this.getVariants(String(str)).map((v) => String(v).toLowerCase());
      return variants.some((v) => v.includes(q));
    },

    getVariants(input) {
      if (input == null) return [];
      // Normalize: keep alphanumeric only
      const s = String(input).normalize('NFKD').replace(/[^A-Za-z0-9]/, '').trim();

      // 1) Only capital letters & numbers
      const onlyCapsNums = s.replace(/[^A-Z0-9]/g, '');

      // 2) Only chars after a space & numbers
      let afterSpaceAndNums = '';
      for (let i = 0; i < s.length; i += 1) {
        const ch = s[i];
        const prevIsSpace = i === 0 || (i > 0 && /\s/.test(s[i - 1]));
        if (/[0-9]/.test(ch) || (prevIsSpace && /[A-Z]/.test(ch))) {
          afterSpaceAndNums += ch;
        }
      }

      // 3) Combination of above
      let combo = '';
      for (let i = 0; i < s.length; i += 1) {
        const ch = s[i];
        const prevIsSpace = i > 0 && /\s/.test(s[i - 1]);
        if (/[0-9A-Z]/.test(ch) || (prevIsSpace && /[A-Z]/.test(ch))) {
          combo += ch;
        }
      }

      // Return unique, non-empty variants; include the original normalized string first.
      const variants = [s, onlyCapsNums, afterSpaceAndNums, combo];
      return [...new Set(variants.filter(Boolean))];
    },

    extractTrailingNumber(name) {
      if (typeof name !== 'string') return null;
      const m = name.match(/(\d+)(?!.*\d)/);
      if (!m) return null;
      return parseInt(m[1] || m[2], 10);
    },

    toggleFilterMenu() {
      this.filterToggle = !this.filterToggle;
    },
    scrollToGroup(groupName) {
      this.filterToggle = false;
      this.$nextTick(() => {
        const refKey = `group-${groupName}`;
        let el = this.$refs[refKey];
        if (Array.isArray(el)) [el] = el; // first element
        if (el && typeof el.scrollIntoView === 'function') {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    },
    async loadImage(element, id) {
      const image = await this.getImage(id);
      if (image === null || image === undefined) return;
      element.setAttribute('src', image);
      const imageClass = (await this.hasImage(id)) ? 'preview' : '';
      element.setAttribute('class', imageClass);
    },
    async getImage(id) {
      const image = await get(`camerapreview.${id}`);
      return (image != null || image !== undefined) ? image : activeCamIcon;
    },
    async hasImage(id) {
      return (await get(`camerapreview.${id}`)) != null;
    },
    viewCameraFromDevice({ id: deviceId } = {}, { id: cameraId } = {}) {
      const { deviceList = [] } = this.$store.state.devices;
      const resolvedDeviceId = deviceId
      ?? (deviceList.find(({ cameras = [] }) => cameras.some(({ id }) => id === cameraId))?.id);
      this.$router.push({ name: 'CameraView', params: { camera: cameraId, device: resolvedDeviceId } });
    },
    isGroupSplit(groupName) {
      if (new Set(this.mergedByGroup[groupName].map((e) => e.device.name)).size < 2) {
        return false;
      }
      const vis = (this.mergedByGroupVisible[groupName] || []).length;
      const total = (this.mergedByGroup[groupName] || []).length;
      return vis > 0 && vis < total; // show only when search reduced the group
    },

    deviceNames(groupName) {
      const list = this.mergedByGroupVisible[groupName] || [];
      const names = new Set(
        list.map((e) => e?.device?.name).filter(Boolean),
      );
      return [...names].sort((a, b) => {
        const nA = this.extractTrailingNumber(a) ?? 0;
        const nB = this.extractTrailingNumber(b) ?? 0;
        return nA - nB;
      });
    },

    lighthouseLastUpdate(deviceId, cameraId) {
      return this.lighthouseData[deviceId]?.mrecs?.find((m) => m.mrec_id === cameraId)?.last_update;
    },
    lighthouseStatus(deviceId, cameraId) {
      const lastUpdateString = this.lighthouseLastUpdate(deviceId, cameraId);
      if (!lastUpdateString) return 'noData';
      const lastUpdate = new Date(lastUpdateString);

      const now = new Date().setMinutes(0, 0, 0); // last update will always be on the hour

      if (!lastUpdate || now - lastUpdate.getTime() > 1000 * 60 * 60 * 6) return 'offline';

      if (!lastUpdate || now - lastUpdate.getTime() > 1000 * 60 * 60 * 1.5) return 'late';

      return 'online';
    },
    lighthouseStatusText(deviceId, cameraId) {
      const status = this.lighthouseStatus(deviceId, cameraId);
      if (status === 'noData') return 'No Data';
      if (status === 'offline') return '!';
      return '';
    },

  },
  mounted() {
    Auth.currentUserInfo().then((user) => {
      this.email = user.attributes.email;
    });
    this.$store.dispatch('lighthouse/fetchUids');
  },
  created() {
    setTimeout(() => {
      this.$store.dispatch('GetDeviceList');
    }, 0);
    try {
      const saved = localStorage.getItem('camerasSearch');
      if (saved !== null) this.searchQuery = saved;
    } catch { /* Ignore */ }
  },
  watch: {
    searchQuery(val) {
      try {
        if (val) localStorage.setItem('camerasSearch', val);
        else localStorage.removeItem('camerasSearch');
      } catch { /* Ignore */ }
    },
  },
};
</script>

<style scoped>

.container {
  --camera-tile-size: min(150px, calc(80vw / 3));
  --camera-tile-gap: min(30px, calc(10vw / 3));
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: stretch;
  height: 100%;
}

.fadeY-enter-active,
.fadeY-leave-active {
  transition: all 0.3s;
}

.fadeY-enter,
.fadeY-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}

.filter-abs {
  display: flex;
  justify-content: center;
  margin-top: 1rem;
  margin-bottom: 1rem;
  width: 100%;
}

.filter-background {
  position: absolute;
  width: 100vw;
  height: 100%;
  top: 0;
  left: 0;
  z-index: 3;
}

.filter-body {
  z-index: 4;
  display: flex;
  flex-direction: column;
  position: absolute;
  border-radius: var(--border-radius);
  background-color: var(--surface);
  backdrop-filter: blur(3px);
  width: 260px;
  top: 58px;
  right: 0px;
}

.filter-body button {
  background-color: transparent;
  font-size: 16px;
  padding: 24px 0px 24px 18px;
  text-align: left;
}

.filter-body button:hover,
.filter-body button:active {
  background: var(--surface-light);
}

.filter-body button + button {
  border-top: var(--border);
}

.filter-container {
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
  width: min(440px, 90vw)
}

.filter-container p {
  width: 50%;
  text-align: left;
}

.filterButton {
  justify-content: center;
  align-items: center;
  display: flex;
  font-size: 16px;
  width: 144px;
  height: 44px;
  background-color: var(--surface);
  border-radius: var(--border-radius);
}

.filter-container p {
  font-size: 16px;
  letter-spacing: 0.015em;
}

.filter-container button div {
  height: 30px;
  width: 30px;
  margin-left: 12px;
  background-image: url("data:image/svg+xml,%3Csvg width='26' height='15' viewBox='0 0 26 15' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M23.2439 0.749742L21.2817 2.72511L18.5201 5.5L12.9968 11.0498L6.49996 4.52648L2.74967 0.749739L0.796265 2.72511L13 15L25.2005 2.72829L23.2439 0.749742Z' fill='%23EAEBEC'/%3E%3C/svg%3E%0A");
}

.device-group+.device-group {
  margin-top: 1rem;
  border-top: var(--border);
  padding-top: 1rem;
}

.device-group:last-child {
  margin-bottom: 3rem;
}

.camera {
  background-image: url("data:image/svg+xml,%3Csvg width='48' height='48' viewBox='0 0 48 48' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M46 14L32 24L46 34V14Z' stroke='black' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M28 10H6C3.79086 10 2 11.7909 2 14V34C2 36.2091 3.79086 38 6 38H28C30.2091 38 32 36.2091 32 34V14C32 11.7909 30.2091 10 28 10Z' stroke='black' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E%0A");
}

.cameraOff {
  background-image: url("data:image/svg+xml,%3Csvg width='48' height='48' viewBox='0 0 48 48' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M21.32 10H28C29.0609 10 30.0783 10.4214 30.8284 11.1716C31.5786 11.9217 32 12.9391 32 14V20.68L34 22.68L46 14V34M32 32V34C32 35.0609 31.5786 36.0783 30.8284 36.8284C30.0783 37.5786 29.0609 38 28 38H6C4.93913 38 3.92172 37.5786 3.17157 36.8284C2.42143 36.0783 2 35.0609 2 34V14C2 12.9391 2.42143 11.9217 3.17157 11.1716C3.92172 10.4214 4.93913 10 6 10H10L32 32Z' stroke='black' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M2 2L46 46' stroke='black' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E%0A");
}

.card p {
  font-weight: 600;
  font-size: 16px;
  margin-bottom: 3px;
  text-align: left;
}

.cameraImage {
  background: var(--surface);
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: var(--border-radius);
  width: var(--camera-tile-size);
  height: var(--camera-tile-size);
  overflow: hidden;
}

.cameraImage img.preview {
  height: 100%;
}

.statusIndicator {
  position: absolute;
  top: -4px;
  left: -4px;
  border-radius: 8px;
  min-width: 4px;
  height: 8px;
  --color: var(--text-faint);
  z-index: 2;
  animation: 3s loadingStatusIndicator infinite;
  transition:
    background-color 0.5s ease,
    box-shadow 0.5s ease,
    opacity 0.5s ease;
  box-shadow: 0 0 0 0 transparent;
  background: var(--color);
  padding: 0 2px;
  color: var(--bg);
  font-size: 8px;
  line-height: 1;
  text-align: center;
}

@keyframes loadingStatusIndicator {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
  100% {
    opacity: 1;
  }
}

.statusIndicator.offline {
  animation: none;
  --color: var(--error);
  box-shadow: 0 0 8px 2px var(--color);
}

.statusIndicator.online {
  animation: none;
  --color: var(--success);
  box-shadow: 0 0 8px 2px var(--color);
}

.statusIndicator.late {
  animation: none;
  --color: var(--warn);
  box-shadow: 0 0 8px 2px var(--color);
}

.statusIndicator.noData {
  animation: none;
}

.hidden {
  display: none;
}

.card {
  position: relative;
  height: calc(var(--camera-tile-size) + 24px);
  width: var(--camera-tile-size);
}

.skeleton-card {
  width: var(--camera-tile-size);
  min-width: 0;
  height: calc(var(--camera-tile-size) + 24px);
}

.skeleton-card:last-child {
  margin-bottom: 0px !important;
}

.cameras {
  display: flex;
  gap: var(--camera-tile-gap);
  flex-wrap: wrap;
  justify-content: space-around;
  width: 100vw;
  flex: 1 1 70vh;
  padding-top: 14px;
  margin: 0 auto;
  max-width: min(90%, 600px);
  overflow-y: scroll;
  overflow-x: hidden;
}

.cameras::-webkit-scrollbar {
  display: none;
}

.cameras-skeleton {
  display: grid;
  grid-template-columns: repeat(3, var(--camera-tile-size));
  gap: 15px;
  justify-content: center;
  width: 100vw;
  margin-top: 14px;
  max-height: 70vh;
  max-width: 500px;
  overflow-y: scroll;
}

.cameras-skeleton::-webkit-scrollbar {
  display: none;
}

.textWrapper {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-left: 24px;
}

h1 {
  font-size: 18px;
}

p {
  font-size: 14px;
}

.skeleton-card {
  width: var(--camera-tile-size);
  min-width: 0;
  height: var(--camera-tile-size);
  background: var(--surface);
  border-radius: var(--border-radius);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  box-shadow: var(--shadow);
  padding: 0;
  gap: 0;
}

.skeleton-title.card-title {
  width: 100%;
  max-width: var(--camera-tile-size);
  height: 16px;
  margin-bottom: 8px;
  background: linear-gradient(
    90deg,
    var(--surface) 25%,
    var(--surface-light) 50%,
    var(--surface) 75%
  );
  border-radius: var(--border-radius);
  animation: skeleton-loading 2s infinite linear;
}

.skeleton-image.cameraImage {
  width: var(--camera-tile-size);
  height: var(--camera-tile-size);
  background: linear-gradient(
    90deg,
    var(--surface) 25%,
    var(--surface-light) 50%,
    var(--surface) 75%
  );
  border-radius: var(--border-radius);
  animation: skeleton-loading 2s infinite linear;
  display: flex;
  justify-content: center;
  align-items: center;
}

.skeleton-text {
  display: none;
}

.skeleton-title {
  width: 120px;
  height: 20px;
  background: linear-gradient(
    90deg,
    var(--surface) 25%,
    var(--surface-light) 50%,
    var(--surface) 75%
  );
  border-radius: var(--border-radius);
  animation: skeleton-loading 2s infinite linear;
  margin-bottom: 8px;
}

.skeleton-dropdown {
  width: 124px;
  height: 44px;
  background: linear-gradient(
    90deg,
    var(--surface) 25%,
    var(--surface-light) 50%,
    var(--surface) 75%
  );
  border-radius: var(--border-radius);
  animation: skeleton-loading 2s infinite linear;
}

@keyframes skeleton-loading {
  0% {
    background-position: -200px 0;
  }

  100% {
    background-position: calc(200px + 100%) 0;
  }
}

.search-bar {
  width: 100%;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: var(--border);
}

.search-input {
  background: var(--surface);
  border: var(--border);
  border-radius: var(--border-radius);
  padding: var(--border-radius);
  width: min(440px, 90vw);
}

.search-input, .search-input::placeholder {
  font-size: 1rem;
}

.search-error {
  font-size: 1rem;
  color: var(--text-faint)
}

h1 {
  font-size: 2rem;
}
</style>
